<template>
  <div class="mux-player">
    <component
      :is="'mux-player'"
      ref="playerRef"
      class="mux-player__player"
      data-test-id="MuxVideoPlayer"
      :playback-id="media.playbackId"
      :playback-token="media.playbackToken"
      :thumbnail-token="media.thumbnailToken"
      stream-type="on-demand"
      preload="metadata"
      playsinline
      nohotkeys
      disable-cookies
      disable-tracking
      min-resolution="720p"
      max-auto-resolution="1080p"
      @timeupdate="updateProgress"
      @durationchange="updateProgress"
      @loadedmetadata="updateProgress"
      @ended="onEnded"
    />
    <ProgressBar class="mux-player__progress-bar" :value="progress" />
  </div>
</template>

<script lang="ts">
import '@mux/mux-player'
import { defineComponent, type PropType, ref, watch } from 'vue'

import ProgressBar from '../../../shared/components/dumb/ProgressBar.vue'

type MuxMedia = {
  playbackId: string
  playbackToken: string | undefined
  thumbnailToken: string | undefined
  type: 'mux'
}

type MuxPlayerElement = HTMLElement & {
  paused: boolean
  duration: number
  currentTime: number
  play: () => Promise<void>
  pause: () => void
}

export default defineComponent({
  components: { ProgressBar },
  props: {
    media: {
      type: Object as PropType<MuxMedia>,
      required: true,
    },
  },
  emits: {
    ended: () => true,
  },
  setup(props, { emit }) {
    const playerRef = ref<MuxPlayerElement>()
    const progress = ref(0)

    const updateProgress = (): void => {
      const player = playerRef.value
      if (!player) return

      const duration = Number(player.duration)
      const currentTime = Number(player.currentTime)

      progress.value =
        Number.isFinite(duration) && duration > 0 && Number.isFinite(currentTime)
          ? Math.max(0, Math.min(1, currentTime / duration))
          : 0
    }

    const onEnded = (): void => {
      progress.value = 1
      emit('ended')
    }

    watch(
      () => props.media.playbackId,
      () => {
        progress.value = 0
      },
    )

    return { onEnded, playerRef, progress, updateProgress }
  },
})
</script>

<style lang="postcss">
.mux-player {
  display: flex;
  max-width: 478px;
  flex-direction: column;
  align-items: center;
  margin: 0 auto;
  gap: 1rem;

  &__player {
    width: 100%;
    max-height: fit-content;
    aspect-ratio: 16 / 9;

    --seek-backward-button: none;
    --seek-forward-button: none;
    --live-button: none;
    --mute-button: none;
    --captions-button: none;
    --airplay-button: none;
    --pip-button: none;
    --cast-button: none;
    --playback-rate-button: none;
    --volume-range: none;
    --time-range: none;
    --time-display: none;
    --duration-display: none;
    --rendition-menu-button: none;
  }

  &__progress-bar {
    width: calc(100% - 2rem);
  }
}
</style>
